import { useEffect } from "react";
import { trackEvent } from "@/lib/analytics";

const THRESHOLDS = [25, 50, 75, 100] as const;

/**
 * Envoie `scroll_depth` { percent_scrolled } aux paliers 25/50/75/100.
 * Reset a chaque changement de `pathKey` (nouvelle page = nouveaux paliers).
 */
export function useScrollDepth(pathKey: string): void {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const fired = new Set<number>();

    const compute = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop;
      const viewport = window.innerHeight || doc.clientHeight;
      const full = doc.scrollHeight;
      const denom = Math.max(1, full - viewport);
      const pct = Math.min(100, Math.round((scrollTop / denom) * 100));
      for (const t of THRESHOLDS) {
        if (pct >= t && !fired.has(t)) {
          fired.add(t);
          trackEvent("scroll_depth", { percent_scrolled: t });
        }
      }
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        compute();
        ticking = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    // check initial (page courte)
    compute();
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathKey]);
}
